import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CalendarIcon,
  DocumentTextIcon,
  SparklesIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { patientsService, reportsService } from '../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const EvolutionAnalysis = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [analysisInsights, setAnalysisInsights] = useState(null);

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar dados do paciente
      const patientData = await patientsService.getById(patientId);
      setPatient(patientData);

      // Carregar relatórios do paciente
      const reportsData = await reportsService.getByPatient(patientId);
      
      // Ordenar por data
      const sortedReports = reportsData.sort((a, b) => 
        new Date(a.examDate) - new Date(b.examDate)
      );
      
      setReports(sortedReports);
      
      // Selecionar automaticamente todos os relatórios
      setSelectedReports(sortedReports);
      
      // Gerar insights de IA
      generateInsights(sortedReports);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = (reportsData) => {
    if (reportsData.length < 2) {
      setAnalysisInsights({
        hasProgression: false,
        message: 'Necessário pelo menos 2 laudos para análise evolutiva.'
      });
      return;
    }

    const firstReport = reportsData[0];
    const lastReport = reportsData[reportsData.length - 1];
    
    // Calcular médias dos limiares
    const calcAverage = (thresholds) => {
      const values = Object.values(thresholds).filter(v => v !== null);
      return values.reduce((a, b) => a + b, 0) / values.length;
    };

    const firstAvgRight = calcAverage(firstReport.exam.rightEar || {});
    const lastAvgRight = calcAverage(lastReport.exam.rightEar || {});
    const firstAvgLeft = calcAverage(firstReport.exam.leftEar || {});
    const lastAvgLeft = calcAverage(lastReport.exam.leftEar || {});

    const diffRight = lastAvgRight - firstAvgRight;
    const diffLeft = lastAvgLeft - firstAvgLeft;
    const avgDiff = (diffRight + diffLeft) / 2;

    // Classificar perda auditiva
    const classifyLoss = (avg) => {
      if (avg <= 25) return { level: 'Normal', color: 'text-green-600', bg: 'bg-green-50' };
      if (avg <= 40) return { level: 'Leve', color: 'text-yellow-600', bg: 'bg-yellow-50' };
      if (avg <= 55) return { level: 'Moderada', color: 'text-orange-600', bg: 'bg-orange-50' };
      if (avg <= 70) return { level: 'Moderadamente Severa', color: 'text-red-600', bg: 'bg-red-50' };
      return { level: 'Severa', color: 'text-red-800', bg: 'bg-red-100' };
    };

    const firstClassification = classifyLoss((firstAvgRight + firstAvgLeft) / 2);
    const lastClassification = classifyLoss((lastAvgRight + lastAvgLeft) / 2);

    // Detectar agravamento
    const hasWorsening = avgDiff > 10; // Mais de 10 dB de piora
    const hasImprovement = avgDiff < -5; // Melhora de 5 dB
    const isStable = Math.abs(avgDiff) <= 10;

    // Calcular dias entre exames
    const daysBetween = Math.floor(
      (new Date(lastReport.examDate) - new Date(firstReport.examDate)) / (1000 * 60 * 60 * 24)
    );

    setAnalysisInsights({
      hasProgression: hasWorsening,
      hasImprovement,
      isStable,
      avgDiff: avgDiff.toFixed(1),
      diffRight: diffRight.toFixed(1),
      diffLeft: diffLeft.toFixed(1),
      firstClassification,
      lastClassification,
      daysBetween,
      totalReports: reportsData.length,
      recommendation: hasWorsening 
        ? 'Sugere-se avaliação de possível nexo causal ocupacional devido ao agravamento detectado.'
        : isStable 
        ? 'Audição mantém-se estável. Recomenda-se manutenção das medidas de proteção auditiva.'
        : 'Observada melhora nos limiares. Continuar monitoramento periódico.'
    });
  };

  // Preparar dados para gráfico de evolução de limiares
  const prepareThresholdData = () => {
    const frequencies = ['250', '500', '1000', '2000', '3000', '4000', '6000', '8000'];
    
    return frequencies.map(freq => {
      const dataPoint = { frequency: `${freq}Hz` };
      
      selectedReports.forEach((report, idx) => {
        const rightValue = report.exam?.rightEar?.[freq] || null;
        const leftValue = report.exam?.leftEar?.[freq] || null;
        
        dataPoint[`OD_${idx + 1}`] = rightValue;
        dataPoint[`OE_${idx + 1}`] = leftValue;
      });
      
      return dataPoint;
    });
  };

  // Preparar dados para gráfico de média temporal
  const prepareTimelineData = () => {
    return selectedReports.map((report, idx) => {
      const rightEar = report.exam?.rightEar || {};
      const leftEar = report.exam?.leftEar || {};
      
      const calcAvg = (ear) => {
        const values = Object.values(ear).filter(v => v !== null);
        return values.length > 0 
          ? values.reduce((a, b) => a + b, 0) / values.length 
          : null;
      };
      
      return {
        date: new Date(report.examDate).toLocaleDateString('pt-BR', { 
          month: 'short', 
          year: 'numeric' 
        }),
        examNumber: idx + 1,
        avgRight: calcAvg(rightEar),
        avgLeft: calcAvg(leftEar),
        srt: report.exam?.srtRight || null,
        lrf: report.exam?.lrfRight || null
      };
    });
  };

  // Preparar dados para radar chart (comparação primeiro vs último)
  const prepareRadarData = () => {
    if (selectedReports.length < 2) return [];
    
    const firstReport = selectedReports[0];
    const lastReport = selectedReports[selectedReports.length - 1];
    const frequencies = ['250', '500', '1000', '2000', '3000', '4000', '6000', '8000'];
    
    return frequencies.map(freq => ({
      frequency: `${freq}Hz`,
      primeiro: ((firstReport.exam?.rightEar?.[freq] || 0) + (firstReport.exam?.leftEar?.[freq] || 0)) / 2,
      ultimo: ((lastReport.exam?.rightEar?.[freq] || 0) + (lastReport.exam?.leftEar?.[freq] || 0)) / 2
    }));
  };

  const colors = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!patient || reports.length < 2) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <ExclamationTriangleIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Análise Evolutiva Indisponível
          </h2>
          <p className="text-gray-600 mb-8">
            É necessário pelo menos 2 laudos do mesmo paciente para realizar a análise evolutiva.
          </p>
          <button
            onClick={() => navigate('/reports')}
            className="btn-secondary inline-flex items-center"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Voltar para Relatórios
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const thresholdData = prepareThresholdData();
  const timelineData = prepareTimelineData();
  const radarData = prepareRadarData();

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/reports')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <SparklesIcon className="w-8 h-8 text-medical-600 mr-3" />
                Análise Evolutiva
              </h1>
              <p className="text-gray-600 mt-1">
                Paciente: <span className="font-semibold">{patient.name}</span> • {reports.length} laudos encontrados
              </p>
            </div>
          </div>
        </div>

        {/* Insights Cards */}
        {analysisInsights && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1: Status Geral */}
            <div className={`card-gradient p-6 border-l-4 ${
              analysisInsights.hasProgression 
                ? 'border-red-500' 
                : analysisInsights.hasImprovement 
                ? 'border-green-500' 
                : 'border-blue-500'
            }`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-2">Status da Evolução</p>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {analysisInsights.hasProgression 
                      ? 'Agravamento' 
                      : analysisInsights.hasImprovement 
                      ? 'Melhora' 
                      : 'Estável'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Variação média: {analysisInsights.avgDiff > 0 ? '+' : ''}{analysisInsights.avgDiff} dB
                  </p>
                </div>
                {analysisInsights.hasProgression ? (
                  <ArrowTrendingUpIcon className="w-12 h-12 text-red-500" />
                ) : analysisInsights.hasImprovement ? (
                  <ArrowTrendingDownIcon className="w-12 h-12 text-green-500" />
                ) : (
                  <InformationCircleIcon className="w-12 h-12 text-blue-500" />
                )}
              </div>
            </div>

            {/* Card 2: Classificação */}
            <div className="card-gradient p-6">
              <p className="text-sm text-gray-600 mb-3">Classificação da Perda</p>
              <div className="space-y-3">
                <div className={`px-3 py-2 rounded-lg ${analysisInsights.firstClassification.bg}`}>
                  <p className="text-xs text-gray-600">Primeiro Exame</p>
                  <p className={`text-lg font-bold ${analysisInsights.firstClassification.color}`}>
                    {analysisInsights.firstClassification.level}
                  </p>
                </div>
                <div className={`px-3 py-2 rounded-lg ${analysisInsights.lastClassification.bg}`}>
                  <p className="text-xs text-gray-600">Último Exame</p>
                  <p className={`text-lg font-bold ${analysisInsights.lastClassification.color}`}>
                    {analysisInsights.lastClassification.level}
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3: Período */}
            <div className="card-gradient p-6">
              <CalendarIcon className="w-8 h-8 text-medical-600 mb-3" />
              <p className="text-sm text-gray-600 mb-2">Período Analisado</p>
              <h3 className="text-2xl font-bold text-gray-800 mb-1">
                {analysisInsights.daysBetween} dias
              </h3>
              <p className="text-sm text-gray-600">
                {analysisInsights.totalReports} exames realizados
              </p>
            </div>
          </div>
        )}

        {/* AI Recommendation */}
        {analysisInsights && (
          <div className={`card-gradient p-6 border-l-4 ${
            analysisInsights.hasProgression ? 'border-red-500 bg-red-50' : 'border-blue-500 bg-blue-50'
          }`}>
            <div className="flex items-start space-x-4">
              <SparklesIcon className={`w-6 h-6 flex-shrink-0 ${
                analysisInsights.hasProgression ? 'text-red-600' : 'text-blue-600'
              }`} />
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Recomendação IA</h4>
                <p className="text-gray-700 leading-relaxed">{analysisInsights.recommendation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Gráfico: Evolução dos Limiares por Frequência */}
        <div className="card-gradient p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <ChartBarIcon className="w-6 h-6 text-medical-600 mr-2" />
            Evolução dos Limiares Auditivos
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={thresholdData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="frequency" stroke="#6b7280" />
              <YAxis 
                stroke="#6b7280" 
                label={{ value: 'Limiar (dB)', angle: -90, position: 'insideLeft' }}
                reversed
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              {selectedReports.map((report, idx) => (
                <React.Fragment key={idx}>
                  <Line
                    type="monotone"
                    dataKey={`OD_${idx + 1}`}
                    stroke={colors[idx % colors.length]}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    name={`OD - Exame ${idx + 1}`}
                  />
                  <Line
                    type="monotone"
                    dataKey={`OE_${idx + 1}`}
                    stroke={colors[idx % colors.length]}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ r: 4 }}
                    name={`OE - Exame ${idx + 1}`}
                  />
                </React.Fragment>
              ))}
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-4 text-center">
            Linha sólida: Orelha Direita (OD) • Linha tracejada: Orelha Esquerda (OE)
          </p>
        </div>

        {/* Gráfico: Timeline de Médias */}
        <div className="card-gradient p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <CalendarIcon className="w-6 h-6 text-tech-600 mr-2" />
            Timeline de Médias Tonais
          </h3>
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart data={timelineData}>
              <defs>
                <linearGradient id="colorRight" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorLeft" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" />
              <YAxis 
                stroke="#6b7280"
                reversed
                label={{ value: 'Média Tonal (dB)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="avgRight"
                stroke="#3b82f6"
                strokeWidth={3}
                fill="url(#colorRight)"
                name="Média OD"
              />
              <Area
                type="monotone"
                dataKey="avgLeft"
                stroke="#ef4444"
                strokeWidth={3}
                fill="url(#colorLeft)"
                name="Média OE"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfico: Radar Comparativo (Primeiro vs Último) */}
        <div className="card-gradient p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
            <DocumentTextIcon className="w-6 h-6 text-health-600 mr-2" />
            Comparação: Primeiro vs Último Exame
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="frequency" stroke="#6b7280" />
              <PolarRadiusAxis angle={90} domain={[0, 120]} stroke="#6b7280" reversed />
              <Radar
                name="Primeiro Exame"
                dataKey="primeiro"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Radar
                name="Último Exame"
                dataKey="ultimo"
                stroke="#ef4444"
                fill="#ef4444"
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-4 text-center">
            Média binaural (OD + OE) / 2 para cada frequência
          </p>
        </div>

        {/* Lista de Exames */}
        <div className="card-gradient p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <DocumentTextIcon className="w-6 h-6 text-medical-600 mr-2" />
            Exames Incluídos na Análise
          </h3>
          <div className="space-y-3">
            {reports.map((report, idx) => (
              <div key={report.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-medical-600 text-white rounded-full flex items-center justify-center font-bold">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">
                      Exame {idx + 1}
                    </p>
                    <p className="text-sm text-gray-600">
                      {new Date(report.examDate).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/reports/${report.id}`}
                  className="btn-secondary text-sm"
                >
                  Ver Laudo
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EvolutionAnalysis;
